import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { CsCardInterface } from 'components/cs-card/cs-card';
import { AnimationController } from 'providers/animation-controller';
import { getCardListGap, getTransitionSlideInOutMs } from 'services/browser-utils';

import { CsCardListInterface, CsCardListInterfaceClass } from './cs-card-list-interface';
import { CardListAnimationState } from './cs-card-list-animation-state';
import { computeCardListLayout } from './cs-card-list-layout';

/** Where one card of a cs-card-list was placed: its distance from the top of the list, and its height. */
export interface CsCardListPlacement {
  element: HTMLElement;
  top: number;
  height: number;
}

@Component({
    selector: 'cs-card-list',
    template: `<ng-content></ng-content>`,
    providers: [{ provide: CsCardListInterfaceClass, useExisting: forwardRef(() => CsCardListComponent) }],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CsCardListComponent implements CsCardListInterface {
  constructor(public elemRef: ElementRef, private cdr: ChangeDetectorRef, private animationController: AnimationController) { }
  @Input('grid') grid: boolean;
  @Input('vertically-centered') verticallyCentered: boolean;

  // reports where the cards were placed, each time that changes (#6111). The cards are moved by
  // writing to the page directly, often from an observer or a timer and after the parent's
  // template was last checked, so a parent that lays something out beside the cards (the
  // insert strips of cs-exercise-list) cannot read their positions from the page itself.
  @Output() placementsChanged = new EventEmitter<CsCardListPlacement[]>();
  private lastPlacementsKey = '';

  private lastPositionUpdateHash: string = null;
  private isUpdatingPositions = false;
  private styleCheckInterval: any;
  private lastPaddingLeft: string = '';
  private lastPaddingRight: string = '';
  private mutationObserver: MutationObserver;
  private resizeObserver: ResizeObserver;
  private visibilityObserver: IntersectionObserver;
  // whether each card snaps or slides to its new place; owns every flag behind that decision.
  // A settle lasts the tab transition duration, to cover the full animation period.
  private animationState = new CardListAnimationState(() => getTransitionSlideInOutMs());
  private destroyed = false;
  private lastContainerWidth = 0;

  // when true, skip animation suppression during drag operations
  // this allows non-dragged cards to animate smoothly during reorder
  public allowDragAnimations = false;

  // element currently being dragged - drag service sets this so updatePositions() can skip it
  public draggingElement: HTMLElement | null = null;

  // true while the list hasn't completed initial layout (e.g., hidden in inactive tab)
  get initializing(): boolean {
    return this.animationState.initializing;
  }

  ngAfterContentInit() {
    // use MutationObserver to detect when cards are added/removed
    this.mutationObserver = new MutationObserver(() => {
      this.injectListToChildren();

      // Note: a card added after initial layout is detected synchronously inside
      // updatePositions() (see the newly-added handling there), NOT here. The
      // first positioning pass after an insert is Angular's synchronous
      // ngAfterContentChecked, which runs before this async observer — handling
      // it there is what lets the existing cards animate to make space (FLIP)
      // instead of snapping. This observer only injects the list reference and
      // triggers a position refresh.

      // skip updatePositions during drag — the drag service controls card transforms
      // directly, and subtree mutations (e.g., badge text updates) would reset them
      if (!this.allowDragAnimations) {
        this.updatePositions();
      }

      // only enable animations after initial layout is complete
      if (!this.initializing) {
        this.enableAnimationsOnChildren();
      }

      // 2026-01-20: removed markForCheck() - cs-card-list has no template bindings (just ng-content),
      // and all positioning is done via direct DOM manipulation. calling markForCheck()
      // caused infinite loops when child components trigger rapid DOM changes.
    });

    this.mutationObserver.observe(this.elemRef.nativeElement, {
      childList: true,
      subtree: true
    });

    // detect container resize (e.g. device fold, window resize) to recalculate card widths
    // only suppress animations on width changes (external resize like viewport/device fold)
    // height changes are caused by our own layout updates and should not suppress animations
    this.lastContainerWidth = this.elemRef.nativeElement.offsetWidth;
    this.resizeObserver = new ResizeObserver(() => {
      if (this.destroyed) return;
      const currentWidth = this.elemRef.nativeElement.offsetWidth;
      if (currentWidth !== this.lastContainerWidth) {
        this.lastContainerWidth = currentWidth;
        this.animationState.markSettling();
      }
      this.updatePositions();
    });
    this.resizeObserver.observe(this.elemRef.nativeElement);

    // initial setup - inject list reference to all children
    this.injectListToChildren();

    // Set up periodic check for padding changes
    // Check every 500ms for style changes (e.g., from DevTools or CSS updates)
    this.styleCheckInterval = setInterval(() => {
      if (this.destroyed) return;
      const containerStyle = getComputedStyle(this.elemRef.nativeElement);
      const currentPaddingLeft = containerStyle.paddingLeft;
      const currentPaddingRight = containerStyle.paddingRight;

      if (currentPaddingLeft !== this.lastPaddingLeft || currentPaddingRight !== this.lastPaddingRight) {
        this.lastPaddingLeft = currentPaddingLeft;
        this.lastPaddingRight = currentPaddingRight;
        this.updatePositions();
        this.cdr.markForCheck();
      }
    }, 500);

    // Store initial padding values
    const containerStyle = getComputedStyle(this.elemRef.nativeElement);
    this.lastPaddingLeft = containerStyle.paddingLeft;
    this.lastPaddingRight = containerStyle.paddingRight;

    this.updatePositions();

    // enable animations on children after initial layout is complete
    // wait for minimum time, then check if layout has stabilized
    setTimeout(() => {
      this.scheduleAnimationEnable();
    }, 300);

    // set up visibility observer to detect when list becomes visible
    // this handles the case where list initializes while hidden (e.g., in inactive tab)
    this.visibilityObserver = new IntersectionObserver((entries) => {
      const isVisible = entries[0].isIntersecting;
      if (isVisible && !this.animationState.hasBeenVisible) {
        this.animationState.hasBeenVisible = true;
        this.onBecameVisible();
      }
    }, { threshold: 0.01 });
    this.visibilityObserver.observe(this.elemRef.nativeElement);
  }

  private onBecameVisible() {
    // if still initializing, the RAF loop in scheduleAnimationEnable will handle it
    // once it detects the element is now visible
    if (this.initializing) return;

    // if already been visible, nothing to do
    if (this.animationState.hasBeenVisible) return;

    // became visible after initialization completed (edge case - shouldn't normally happen)
    // suppress card position animations temporarily
    this.animationState.hasBeenVisible = true;
    this.animationState.markSettling();
    this.updatePositions();
  }

  private isElementVisible(): boolean {
    const style = getComputedStyle(this.elemRef.nativeElement);
    return style.visibility !== 'hidden' && style.display !== 'none';
  }

  private scheduleAnimationEnable(): void {
    // stop only if component was destroyed
    if (this.destroyed) {
      return;
    }

    // keep trying if element not connected yet (page transition in progress)
    if (!this.elemRef.nativeElement.isConnected) {
      requestAnimationFrame(() => this.scheduleAnimationEnable());
      return;
    }

    const visible = this.isElementVisible();

    // wait until layout is stable AND element is actually visible
    // (handles case where list initializes while hidden in inactive tab)
    if (this.animationState.isLayoutStable && visible) {
      // initial position animations stay suppressed for one settle
      this.animationState.finishInitializing();
      this.enableAnimationsOnChildren();
    } else {
      // keep checking until layout stabilizes and element is visible
      requestAnimationFrame(() => this.scheduleAnimationEnable());
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
    }
    if (this.styleCheckInterval) {
      clearInterval(this.styleCheckInterval);
    }
    this.animationState.dispose();
    // Clean up animation counter if destroyed mid-update
    if (this.isUpdatingPositions) {
      this.isUpdatingPositions = false;
      this.animationController.onAnimationEnded();
    }
  }

  private injectListToChildren(): void {
    const children = this.elemRef.nativeElement.children;
    for (let i = 0; i < children.length; i++) {
      const el = children[i] as any;
      const component = el.__csCardComponent;
      if (component?.setList) {
        component.setList(this);
      }
    }
  }

  private enableAnimationsOnChildren(): void {
    const children = this.elemRef.nativeElement.children;
    for (let i = 0; i < children.length; i++) {
      children[i].classList.add('animate');
    }
  }

  ngAfterContentChecked() {
    let currentHash = '';
    const orgArray = this.getCardsArray();
    for (let card of orgArray) {
      let elem = card.getCardElement();
      let style = getComputedStyle(elem, null);
      currentHash += `${elem.offsetWidth},${card.getCurrentCardHeight()},${style.display},${style.visibility};`;
    }
    // Include container dimensions and padding in the hash
    const containerStyle = getComputedStyle(this.elemRef.nativeElement);
    currentHash += `${this.elemRef.nativeElement.offsetWidth},${this.elemRef.nativeElement.offsetHeight}`;
    currentHash += `,${containerStyle.paddingLeft},${containerStyle.paddingRight}`;

    // track layout stability for animation enabling
    this.animationState.noteContentHash(currentHash);

    if (currentHash !== this.lastPositionUpdateHash) {
      this.updatePositions();
      this.lastPositionUpdateHash = currentHash;
    }
  }

  // Force every card to re-measure its own height, then re-run the position layout. Use this
  // after something changed a card's height without the list noticing — most importantly when
  // leaving reorder mode: the cards shrink back to their normal height, but their cached
  // heights were captured while reorder mode made them taller, so the list would otherwise
  // stack them with stale gaps that only corrected on the next scroll.
  remeasureAndReposition(): void {
    this.updateLayouts();   // re-measure every card's height
    this.updatePositions(); // then re-stack them with the fresh heights
  }

  updatePositions() {
    // only track animations after initialization (when cards have the 'animate' class)
    // hidden lists (e.g., in inactive tabs) stay in initializing state and don't animate
    // also skip tracking when hidden — no visible animations to wait for, and tracking
    // can leak if the list keeps getting CD cycles while hidden (e.g., tab switched away)
    if (!this.isUpdatingPositions && !this.initializing && this.isElementVisible()) {
      this.isUpdatingPositions = true;
      this.animationController.onAnimationStarted();
    }

    const cardGap = getCardListGap();
    let arr: {
      card: CsCardInterface,
      elem: HTMLElement,
      width: number,
      height: number,
    }[] = [];

    let orgArray = this.getCardsArray();

    // Measure the natural width of cards WITHOUT layout thrashing.
    // Note: visibility: hidden cards still have valid layout and should be measured;
    // only skip display: none cards which have no layout.
    //
    // This runs in three separate passes (read displays → write width clears → read
    // widths) instead of one interleaved loop that, per card, cleared the width (a
    // write) and then read offsetWidth (a read). That old write→read-per-card pattern
    // forced a full relayout once PER CARD — O(n) forced reflows — which on a heavy
    // list (the Training exercise list) was ~100 ms of main-thread work. When that
    // landed during a page-transition slide it froze the slide and made it snap (#4242
    // Training mid-slide jam). Batching the writes apart from the reads makes the whole
    // measurement force ~2 reflows total regardless of card count, with an IDENTICAL
    // `arr` result.

    // Pass A (reads only): collect the cards we will measure and whether each is the
    // dragged element. getComputedStyle for `display` is a read; the drag test is a
    // pure DOM-tree check (no layout). One layout flush for the whole pass.
    const measured: { card: CsCardInterface, elem: HTMLElement, isDragging: boolean }[] = [];
    for (let card of orgArray) {
      let elem = card.getCardElement();
      let style = getComputedStyle(elem, null);
      if (style.display === 'none') {
        continue;
      }
      // check if this is the dragged element - don't touch its styles
      const isDragging = !!this.draggingElement && (
        this.draggingElement === elem ||
        this.draggingElement.contains(elem) ||
        elem.contains(this.draggingElement)
      );
      measured.push({ card, elem, isDragging });
    }

    // Pass B (writes only): reset explicit width/right on every non-dragged card so its
    // natural width can be read next. No reads here, so these writes don't each trigger
    // a reflow — layout is dirtied once. The dragged element is left untouched (the
    // positioning loop also skips it, so we must not clear its styles).
    for (const m of measured) {
      if (!m.isDragging) {
        m.elem.style.width = '';
        m.elem.style.right = '';
      }
    }

    // Pass C (reads only): read every card's natural width in one batch (one layout
    // flush for all of them, since no writes are interleaved) and its cached height.
    for (const m of measured) {
      const width = m.elem.offsetWidth;
      // if the card's cached height is still 0 (freshly created, not yet
      // measured), it is skipped this pass: it gets no transform AND no
      // `positioned` class, so the stylesheet keeps it invisible instead of
      // letting it paint at the list origin over the first card. This is a
      // conscious choice — the card is revealed by a later updatePositions
      // pass, which the height cache guarantees: updateHeights() marks the
      // card for check when it measures, and this list's hash in
      // ngAfterContentChecked includes getCurrentCardHeight(), so the height
      // arriving re-runs positioning.
      const height = m.card.getCurrentCardHeight();
      if (height > 0) {
        arr.push({ card: m.card, elem: m.elem, width, height });
      }
    }

    if (arr.length <= 0) {
      setTimeout(() => {
        if (this.isUpdatingPositions) {
          this.isUpdatingPositions = false;
          this.animationController.onAnimationEnded();
        }
      }, 0);
      return;
    }

    // Detect cards inserted after the initial layout, synchronously in the
    // same pass that will reposition everything (this runs in Angular's
    // ngAfterContentChecked, before the async MutationObserver). At this point
    // the existing cards are still at their pre-insert positions, so the new
    // cards are marked to SNAP into place (no fly-in from origin) and the
    // existing cards' transitions are re-enabled here (a prior settle may have
    // left an inline `transition: none`), so setting their new transform below
    // animates them old→new (FLIP) — the same smooth move that closes the gap
    // when a card is removed.
    const transitionsToReenable = this.animationState.beginPass(arr.map(c => c.elem), this.allowDragAnimations);
    for (const elem of transitionsToReenable) {
      elem.style.transition = '';
    }

    // measure the container, then work out every card's place (the arithmetic is pure, see
    // cs-card-list-layout.ts). The container height is only read for a vertically-centred list.
    const verticallyCentered = this.verticallyCentered !== undefined;
    const containerHeight = verticallyCentered ? this.elemRef.nativeElement.offsetHeight as number : 0;
    const containerWidth = this.elemRef.nativeElement.offsetWidth as number;
    const computedStyle = getComputedStyle(this.elemRef.nativeElement);
    const layout = computeCardListLayout({
      cards: arr.map(c => ({ width: c.width, height: c.height })),
      containerWidth,
      containerHeight,
      paddingLeft: parseFloat(computedStyle.paddingLeft) || 0,
      paddingRight: parseFloat(computedStyle.paddingRight) || 0,
      gap: cardGap,
      grid: this.grid != null,
      verticallyCentered,
    });
    const cardWidth = layout.cardWidth;

    // collect elements that need transition re-enabled (for batched RAF)
    const elementsToReenableTransition: HTMLElement[] = [];
    const placements: CsCardListPlacement[] = [];

    for (let i = 0; i < arr.length; i++) {
      const { elem, height } = arr[i];

      // skip the element being dragged - drag service controls its position
      const isDragging = this.draggingElement && (
        this.draggingElement === elem ||
        this.draggingElement.contains(elem) ||
        elem.contains(this.draggingElement)
      );

      const posX = layout.positions[i].x;
      const posY = layout.positions[i].y;

      // only update position if not being dragged
      if (!isDragging) {
        // suppress animation while layout is settling, or for a newly-added card
        // on its first positioning pass (so it snaps into place instead of flying
        // in from the origin). Existing cards are not suppressed, so they animate
        // to make space. Drag operations opt out via allowDragAnimations.
        if (this.animationState.shouldSnap(elem, this.allowDragAnimations)) {
          elem.style.transition = 'none';
          elementsToReenableTransition.push(elem);
        }

        elem.style.transform = `translate(${posX}px, ${posY}px)`;
        elem.style.width = `${cardWidth}px`;
        elem.style.right = 'auto';  // Override the right: 0 from base styles
      }

      // the card now has a real position (from us, or from the drag service for
      // a dragged card). Reveal it: the stylesheet keeps a not-yet-positioned
      // card invisible so it cannot paint at the list origin over the first
      // card (one-frame wrong-position flash). See %card-base-styles.
      elem.classList.add('positioned');
      placements.push({ element: elem, top: posY, height });
    }

    // re-enable transitions in a single batched RAF callback
    // NOTE: deferring to next frame ensures transforms are painted before
    // transitions are re-enabled (synchronous reflow can be optimized away)
    if (elementsToReenableTransition.length > 0) {
      requestAnimationFrame(() => {
        for (const el of elementsToReenableTransition) {
          el.style.transition = '';
          // a newly-added card has now been snapped into place; allow it to
          // animate on subsequent position changes like any other card.
          this.animationState.snapped(el);
        }
      });
    }

    this.emitPlacementsIfChanged(placements);

    this.elemRef.nativeElement.style.height = layout.containerHeight === null ? '100%' : `${layout.containerHeight}px`;

    setTimeout(() => {
      if (this.isUpdatingPositions) {
        this.isUpdatingPositions = false;
        this.animationController.onAnimationEnded();
      }
    }, 0);
  }

  // tell the parent where the cards are, but only when that changed. A listener usually marks
  // its view for check; an unconditional emit would then re-run positioning on every check and
  // never settle (the infinite loop noted in ngAfterContentInit's observer).
  private emitPlacementsIfChanged(placements: CsCardListPlacement[]): void {
    if (!this.placementsChanged.observed) return;
    const key = placements.map(p => `${p.top},${p.height}`).join(';');
    if (key === this.lastPlacementsKey) return;
    this.lastPlacementsKey = key;
    this.placementsChanged.emit(placements);
  }

  updateLayouts() {
    let orgArray = this.getCardsArray();
    for (let card of orgArray) {
      card.updateLayout();
    }
  }

  private getCardsArray(): CsCardInterface[] {
    const cards: CsCardInterface[] = [];
    const children = this.elemRef.nativeElement.children;
    for (let i = 0; i < children.length; i++) {
      const el = children[i] as any;
      const component = el.__csCardComponent as CsCardInterface;
      if (component) {
        cards.push(component);
      }
    }
    return cards;
  }
}